import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_LABEL = {
  pending_grantor_signature: 'Wartet auf Unterschrift Vollmachtgeber',
  pending_grantee_signature: 'Wartet auf Unterschrift Bevollmächtigter',
  pending_admin_review: 'Zur Prüfung',
  active: 'Aktiv',
  rejected: 'Abgelehnt',
  revoked: 'Widerrufen',
};

const STATUS_BADGE = {
  pending_grantor_signature: 'bg-amber-100 text-amber-800',
  pending_grantee_signature: 'bg-amber-100 text-amber-800',
  pending_admin_review: 'bg-blue-100 text-blue-800',
  active: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  revoked: 'bg-gray-200 text-gray-700',
};

const fmtDate = (d) => (d ? format(new Date(d), 'dd.MM.yyyy HH:mm', { locale: de }) : '—');

const AdminAccountAuthorizations = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [detail, setDetail] = useState(null);

  useEffect(() => { load(); }, [filter]);

  const load = async () => {
    setLoading(true);
    try {
      const q = filter ? `?status_filter=${filter}` : '';
      const res = await axios.get(`${API}/admin/account-authorizations${q}`);
      setItems(res.data);
    } catch (e) {
      toast.error('Fehler beim Laden der Vollmachten');
    } finally { setLoading(false); }
  };

  const approve = async (id) => {
    if (!window.confirm('Diese Vollmacht jetzt aktivieren? Der/die Bevollmächtigte erhält daraufhin lesenden Zugriff.')) return;
    try {
      await axios.post(`${API}/admin/account-authorizations/${id}/approve`);
      toast.success('Vollmacht aktiviert');
      setDetail(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Aktivieren');
    }
  };

  const reject = async (id) => {
    const note = window.prompt('Optionale Begründung für die Ablehnung:');
    if (note === null) return;
    try {
      await axios.post(`${API}/admin/account-authorizations/${id}/reject`, { note });
      toast.success('Vollmacht abgelehnt');
      setDetail(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Ablehnen');
    }
  };

  const del = async (id) => {
    if (!window.confirm('Vollmacht ENDGÜLTIG löschen? Dies kann nicht rückgängig gemacht werden.')) return;
    try {
      await axios.delete(`${API}/admin/account-authorizations/${id}`);
      toast.success('Vollmacht gelöscht');
      setDetail(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-authorizations">
      <div className="flex justify-between items-start flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-heading font-bold mb-1">Kontovollmachten</h2>
          <p className="text-muted-foreground text-sm">
            Prüfung & Aktivierung der von Kunden erteilten Vollmachten (Lesezugriff).
          </p>
        </div>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="input-field text-sm max-w-xs"
          data-testid="auth-filter-select"
        >
          <option value="">Alle Status</option>
          <option value="pending_admin_review">Zur Prüfung</option>
          <option value="active">Aktiv</option>
          <option value="pending_grantor_signature">Wartet auf Unterschrift Vollmachtgeber</option>
          <option value="pending_grantee_signature">Wartet auf Unterschrift Bevollmächtigter</option>
          <option value="rejected">Abgelehnt</option>
          <option value="revoked">Widerrufen</option>
        </select>
      </div>

      {loading ? (
        <div className="card text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
        </div>
      ) : items.length === 0 ? (
        <div className="card text-center py-12">
          <p className="text-muted-foreground">Keine Vollmachten gefunden.</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {items.map((a) => (
            <div
              key={a.id}
              className="card hover:shadow-md transition cursor-pointer"
              onClick={() => setDetail(a)}
              data-testid={`admin-auth-${a.id}`}
            >
              <div className="flex justify-between items-start gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-gray-500">#{a.id}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_BADGE[a.status]}`}>
                      {STATUS_LABEL[a.status] || a.status}
                    </span>
                  </div>
                  <p className="font-medium mt-2 text-sm">
                    <span className="text-muted-foreground">Von:</span> {a.grantor_full_name}{' '}
                    <span className="text-xs text-muted-foreground">({a.grantor_email})</span>
                  </p>
                  <p className="text-sm">
                    <span className="text-muted-foreground">An:</span> {a.grantee_full_name}{' '}
                    <span className="text-xs text-muted-foreground">({a.grantee_email})</span>
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Erstellt: {fmtDate(a.created_at)}
                  </p>
                </div>
                {a.status === 'pending_admin_review' && (
                  <div className="flex gap-2">
                    <button
                      onClick={(e) => { e.stopPropagation(); approve(a.id); }}
                      className="btn-primary text-xs"
                      data-testid={`approve-auth-${a.id}`}
                    >
                      Aktivieren
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); reject(a.id); }}
                      className="btn-secondary text-xs text-red-600 hover:bg-red-50"
                      data-testid={`reject-auth-${a.id}`}
                    >
                      Ablehnen
                    </button>
                  </div>
                )}
                <button
                  onClick={(e) => { e.stopPropagation(); del(a.id); }}
                  className="text-xs text-gray-400 hover:text-red-600 ml-2"
                  data-testid={`delete-auth-${a.id}`}
                  title="Vollmacht endgültig löschen"
                >
                  Löschen
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {detail && (
        <AdminAuthDetailModal
          auth={detail}
          onClose={() => setDetail(null)}
          onApprove={() => approve(detail.id)}
          onReject={() => reject(detail.id)}
          onDelete={() => del(detail.id)}
        />
      )}
    </div>
  );
};

const AdminAuthDetailModal = ({ auth, onClose, onApprove, onReject, onDelete }) => {
  const canDecide = auth.status === 'pending_admin_review';
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="admin-auth-detail-modal">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 space-y-4">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-xl font-heading font-bold">Vollmacht #{auth.id}</h3>
              <span className={`mt-2 inline-block text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_BADGE[auth.status]}`}>
                {STATUS_LABEL[auth.status] || auth.status}
              </span>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="font-medium mb-1">Vollmachtgeber/in</p>
              <p>{auth.grantor_full_name}</p>
              <p className="text-xs text-muted-foreground">{auth.grantor_email}</p>
              {auth.grantor_birthdate && <p className="text-xs">geb. {auth.grantor_birthdate}</p>}
              {auth.grantor_address_street && (
                <p className="text-xs mt-1">{auth.grantor_address_street}<br />
                  {auth.grantor_address_zip} {auth.grantor_address_city}, {auth.grantor_address_country}
                </p>
              )}
              <p className="text-xs mt-2">
                {auth.grantor_signed_at ? <span className="text-success">Unterschrieben: {fmtDate(auth.grantor_signed_at)}</span> : <span className="text-muted-foreground">Noch nicht unterschrieben</span>}
              </p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="font-medium mb-1">Bevollmächtigte/r</p>
              <p>{auth.grantee_full_name}</p>
              <p className="text-xs text-muted-foreground">{auth.grantee_email}</p>
              {auth.grantee_birthdate && <p className="text-xs">geb. {auth.grantee_birthdate}</p>}
              {auth.grantee_address_street && (
                <p className="text-xs mt-1">{auth.grantee_address_street}<br />
                  {auth.grantee_address_zip} {auth.grantee_address_city}, {auth.grantee_address_country}
                </p>
              )}
              <p className="text-xs mt-2">
                {auth.grantee_signed_at ? <span className="text-success">Unterschrieben: {fmtDate(auth.grantee_signed_at)}</span> : <span className="text-muted-foreground">Noch nicht unterschrieben</span>}
              </p>
            </div>
          </div>

          {auth.pdf_available && (
            <div className="border rounded-lg overflow-hidden bg-gray-100" style={{ height: '60vh' }}>
              <iframe
                title="Vollmachts-PDF"
                src={`${API}/account-authorizations/${auth.id}/pdf`}
                className="w-full h-full"
                data-testid="admin-auth-pdf-preview"
              />
            </div>
          )}

          {auth.admin_approved_at && (
            <p className="text-xs text-success">Aktiviert am {fmtDate(auth.admin_approved_at)}</p>
          )}
          {auth.admin_rejected_at && (
            <div className="text-xs text-red-700 p-3 bg-red-50 rounded-lg">
              Abgelehnt am {fmtDate(auth.admin_rejected_at)}
              {auth.admin_note && <p className="mt-1">Begründung: {auth.admin_note}</p>}
            </div>
          )}
          {auth.revoked_at && (
            <p className="text-xs text-muted-foreground">Widerrufen am {fmtDate(auth.revoked_at)}</p>
          )}

          <div className="flex flex-wrap gap-2 pt-2">
            {auth.pdf_available && (
              <a
                href={`${API}/account-authorizations/${auth.id}/pdf`}
                target="_blank" rel="noopener noreferrer"
                className="btn-secondary text-sm"
                data-testid="open-pdf-in-new-tab"
              >
                PDF in neuem Tab
              </a>
            )}
            {canDecide && (
              <>
                <button onClick={onApprove} className="btn-primary text-sm" data-testid="approve-detail-button">
                  Aktivieren
                </button>
                <button onClick={onReject} className="btn-secondary text-sm text-red-600 hover:bg-red-50" data-testid="reject-detail-button">
                  Ablehnen
                </button>
              </>
            )}
            <button
              onClick={onDelete}
              className="btn-secondary text-sm text-red-600 hover:bg-red-50"
              data-testid="delete-detail-button"
              title="Vollmacht endgültig löschen"
            >
              Löschen
            </button>
            <button onClick={onClose} className="btn-secondary text-sm ml-auto">Schließen</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAccountAuthorizations;
